<script setup lang="ts">
import {pb} from "@/utils/PocketBaseAdapter";
import {ref} from "vue";
import {moment} from "@/utils/useTimeZone";
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { useUserStore } from "@/utils/UserStore";

const globalStore = useGlobalSetupStore();
const { globalSetup } = storeToRefs(globalStore);
const userStore = useUserStore();
const { currentLocationDetails } = storeToRefs(userStore);

interface Props {
  userId?: string;
  bookingId?: string;
  users?: string[];
}

interface Emit {
  (e: "closeDialog", val: boolean): void;
}

const emit = defineEmits<Emit>();
const props = defineProps<Props>();

const toolbarOptions = () => {
  return {
    container: menuOptions(),
    handlers: {
      "placeholder": function (value: any) {
        if (value) {
          const cursorPosition = this.quill.getSelection().index;
          if (value == '{location.email}') {
            this.quill.insertText(cursorPosition, value, 'link', "mailto:" + value);
          } else if (value.toLowerCase().indexOf("link") != -1) {
            this.quill.insertText(cursorPosition, value, 'link', value);
          } else {
            this.quill.insertText(cursorPosition, value);
          }
          this.quill.setSelection(cursorPosition + value.length);
        }
      }
    }
  }
}

const allTemplates:any = ref([])
const messageDialog = ref(false)
const memberToMessage:any = ref({})
const messageSubject = ref('')
const messageType = ref('Email')
const messageText = ref('')
const isPreview = ref(false)
const canEdit = ref(true)
const messageTo = ref('')
const phoneTo = ref('');
const messagePerson = ref({})
const personId = ref('')
const bookingUsers = ref(<any>[])
const messageBookingDetails = ref({})
const messageMember = async(member:any, booking?:any) => {
  memberToMessage.value = member
  messageBookingDetails.value = booking

  let firstUser =  booking?.expand?.users &&  booking?.expand?.users[0] && booking?.expand?.users[0]?.email ? booking?.expand?.users[0] : member?.email ? member : {}

  messagePerson.value = firstUser || {}
  memberToMessage.value = messagePerson.value
  personId.value = memberToMessage.value?.id
  bookingUsers.value = [];

  if(booking?.expand?.users) {
    booking?.expand?.users.forEach((user: any) => {
      user.name = user.firstName + " " + user.lastName
      bookingUsers.value.push(user)
    })
  }

  if(booking?.expand?.owner){
    let canAdd = true
    bookingUsers.value.forEach((user:any) => {
      if(user.id == booking?.expand?.owner.id){
        canAdd = false
      }
    })
    if(canAdd){
      booking.expand.owner.name = booking.expand.owner.firstName + " " + booking.expand.owner.lastName
      bookingUsers.value.push(booking?.expand?.owner)
    }
  }

  if(member?.id){
    let canAdd = true
    bookingUsers.value.forEach((user:any) => {
      if(user.id == member?.id){
        canAdd = false
      }
    })
    if(canAdd){
      if(member.firstName.trim() == '' && member.lastName.trim() == ''){
        member.name = member?.email || ''
      } else {
        member.name = member.firstName + " " + member.lastName
      }
      bookingUsers.value.push(member)
    }
  }

  if(booking?.visitorEmail){
    bookingUsers.value.push({
      id:'visitor',
      email:booking?.visitorEmail,
      name: (booking.visitors || ''),
      firstName: (booking.visitors || ''),
      mobileNumber: (booking?.visitorPhone || ''),
      lastName: ''})
  }

  messageDialog.value = true
}

const messageMembers = async(members:any) => {
  // Get All users from the DB
  let query = members.map((m:string) => 'id="' + m + '"').join('||')
  let allMembers = await pb.collection('users').getFullList({filter:query})
  if(allMembers){
    bookingUsers.value = [];
    personId.value = allMembers[0]?.id
    allMembers.forEach(member => {
      if(member.firstName.trim() == '' && member.lastName.trim() == ''){
        member.name = member?.email || ''
      } else {
        member.name = member.firstName + " " + member.lastName
      }
      bookingUsers.value.push(member)
    })
  }
  messageDialog.value = true
}

const messageBooking = async (bookingId:string) => {
  let record = await pb.collection("bookings").getOne(bookingId,
    {
      fields: 'id, ' +
        'startTime,' +
        'entryCode,' +
        'endTime,' +
        'state,' +
        'created, ' +
        'visitors,' +
        'due,' +
        'value,'+
        'visitorEmail,' +
        'visitorPhone,' +
        'expand.users,' +
        'expand.owner,' +
        'duration, ' +
        'expand.area.areaNumber, ' +
        'expand.activity.name, ' +
        'expand.activity.expand.location.name, ' +
        'expand.docketLine.docket,' +
        'expand.docketLine.docket.expand.docketNumber',
      expand: 'owner,area,activity,docketLine,docketLine.docket,users,users.membership, owner.membership, activity.location',
    })
  if(record){
    await messageMember(record?.expand?.owner, record)
  }
}

const messageOwner = async (userId:string)=>{
  let record = await pb.collection("users").getOne(userId)
  if(record){
    await messageMember(record)
  }
}

const customInserts = ref([{
  'placeholder': [
    '{Member.FirstName}',
    '{Member.LastName}',
    '{Member.PaidBalance}',
    '{Member.LoyaltyBalance}',
    '{Member.CurrentMembership}',
    '{Member.RenewalMembership}',
    '{Member.RenewIssue}',
    '{Member.FailedPayments}',
    '{Member.RenewalTerm}',
    '{Member.RenewalInterval}',
    '{Member.RenewalDate}',
    '{Member.ProfileLink}',
    '{Company.CompanyName}',
    '{Location.Email}',
    '{Location.Address}',
    '{Location.Name}',
    '{Location.SignInLink}',
    '{Location.SignUpLink}',
    '{Booking.StartTime}',
    '{Booking.Duration}',
    '{Booking.Activity}',
    '{Booking.LocationName}',
    '{Booking.AmountDue}',
    '{Booking.EntryCode}',
    '{Booking.Link}',
  ]
}])

const menuOptions = () => {
  if(messageType.value == 'SMS') {
    return [customInserts.value]
  }

  return [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    [{'font':[]}],
    [{'size': ['small', false, 'large', 'huge']}],  // custom dropdown
    [{'align':[]}],
    ['link'],
    [],

    [{'header': [1, 2, 3, 4, 5, 6, false]}],
    [{'list': 'ordered'}, {'list': 'bullet'}, {'list': 'check'}],

    [{'color':[]}, {'background':[]}],
    ['blockquote'],
    ['code-block'],
    ['clean'],
    [],
    customInserts.value
  ]
}

const updateTemplateFields = (val:any) => {
  bookingUsers.value.forEach((user:any) => {
    if(user.id == val){
      messagePerson.value = user
      personId.value = user.id
    }
  })
}

const getPreviewText = () => {
  let messagePreview = messageText.value
  let messageSubjectText = messageSubject.value
  if(messagePerson.value?.id) {
    // Update Text to All Variables
    let vars = [
      '{Member.FirstName}',
      '{Member.LastName}',
      '{Member.PaidBalance}',
      '{Member.LoyaltyBalance}',
      '{Member.CurrentMembership}',
      '{Member.RenewalMembership}',
      '{Member.RenewIssue}',
      '{Member.FailedPayments}',
      '{Member.RenewalTerm}',
      '{Member.RenewalInterval}',
      '{Member.RenewalDate}',
      '{Member.ProfileLink}',
      '{Company.CompanyName}',
      '{Location.Email}',
      '{Location.Address}',
      '{Location.Name}',
      '{Location.SignInLink}',
      '{Location.SignUpLink}',
      '{Booking.StartTime}',
      '{Booking.Duration}',
      '{Booking.Activity}',
      '{Booking.LocationName}',
      '{Booking.AmountDue}',
      '{Booking.EntryCode}',
      '{Booking.Link}']

    let bookingStartTime = messageBookingDetails.value?.startTime ? moment(messageBookingDetails.value.startTime).format('dddd YYYY-MM-DD hh:mm:ss a') : '';

    let replacements = [
      messagePerson.value?.firstName || '',                           // '{Member.FirstName}'
      messagePerson.value?.lastName || '',                            // '{Member.lastName}'
      '',                                                             // '{Member.PaidBalance}',
      messagePerson.value?.loyaltyBalance || '',                      // '{Member.LoyaltyBalance}',
      messagePerson.value?.expand?.membership?.name || '',            // '{Member.CurrentMembership}',
      '',                                                             // '{Member.RenewalMembership}',
      '',                                                             // '{Member.RenewIssue}',
      '',                                                             // '{Member.FailedPayments}',
      messagePerson.value?.expand?.membership?.renewTerm || '',       // '{Member.RenewalTerm}',
      messagePerson.value?.expand?.membership?.renewInterval || '',   // '{Member.renewInterval}',
      messagePerson.value?.membershipRenewDate || '',                 // '{Member.RenewalDate}',
      window.location.origin + '/user/' + messagePerson.value?.id || '',   // '{Member.ProfileLink}',
      globalSetup.value?.companyName || '',                                // '{Company.CompanyName}',
      currentLocationDetails.value?.email || '',                      //'{Location.Email}',
      currentLocationDetails.value?.address || '',                    //'{Location.Address}',
      currentLocationDetails.value?.name || '',                       //'{Location.Name}',
      window.location.origin + '/login',                              //{Location.SignInLink}',
      window.location.origin + '/register',                           //'{Location.SignUpLink}',
      bookingStartTime,                                               // '{Booking.StartTime}',
      (messageBookingDetails.value?.duration || '') + ' mins',        // '{Booking.Duration}',
      messageBookingDetails.value?.expand?.activity?.name || '',      // '{Booking.Activity}',
      messageBookingDetails.value?.expand?.activity?.expand?.location?.name || '', // '{Booking.LocationName}',
      messageBookingDetails.value?.due || '',                         // '{Booking.AmountDue}',
      messageBookingDetails.value?.entryCode || '',                   // '{Booking.EntryCode}',
      window.location.origin + "/?booking=" + messageBookingDetails.value?.id,  // '{Booking.Link}'
    ]

    vars.forEach((item,index) => {
      let regEx = new RegExp(item, "ig");
      messagePreview = messagePreview.replace(regEx, replacements[index] || '')
      messageSubjectText = messageSubjectText.replace(regEx, replacements[index] || '')
    })
  }
  return [messagePreview, messageSubjectText]
}
const getTemplate = (val) => {
  try {
    messageText.value = JSON.parse(JSON.stringify(allTemplates.value.filter((tmpl: any) => tmpl.id == val).pop()?.templateText || ""))
  } catch(e){}
  messageType.value = allTemplates.value.filter((tmpl:any) => tmpl.id == val).pop()?.msgType || messageType.value
  messageSubject.value = allTemplates.value.filter((tmpl:any) => tmpl.id == val).pop()?.subjectHeader || messageSubject.value

  canEdit.value = false
  nextTick(()=>{
    canEdit.value = true
  })
}

const getStyle = () => {
  let styles = ''
  customInserts.value[0].placeholder.forEach(ci => {
    styles += '.ql-picker-item[data-value="' + ci + '"]:before{content:"' +
      ci.replace('{','')
        .replace('}','')
        .replace('.',' ')
        .replace('Membership', ' Membership')
        .replace('SignIn', ' Sign In ')
        .replace('Balance', ' Balance')
        .replace('Interval', ' Interval')
        .replace('Payments', ' Payments')
        .replace('Code', ' Code')
        .replace('Issue', ' Issue')
        .replace('Renewal', 'Renewal ')
      + '"; padding:2px} '


    styles += '.ql-picker-item[data-value="' + ci + '"]{' +
      'background-color:' + (ci.indexOf("Member.") != -1 ? '#91e39e': (ci.indexOf("Booking.") != -1 ? '#9daecd': (ci.indexOf("Company.") != -1 ? '#cd9dc3': '#fff'))) + '}'
  })
  return styles
}

const checkMessageText = () => {
  try {
    return document.getElementById("messageCheck")?.innerText.toString().trim() || ''
  } catch(e){}
  return messageText.value;
}

const closeDialog = () => {
  messageDialog.value = false
  emit('closeDialog', false)
}

const successSent = ref(false)
const failSent = ref(false)
const sendMessage = async () => {
  successSent.value = false
  failSent.value = false
  if (checkMessageText() != '' && messagePerson.value?.id) {
    let params:any = {
      status: 'New',
      msgType: messageType.value,
      subject: messageSubject.value,
      message: messageText.value,
      booking: messageBookingDetails.value?.id,
      from: currentLocationDetails.value?.adminEmail || currentLocationDetails.value?.email || '',
    }

    if (messagePerson.value?.id && (messagePerson.value?.id != 'visitor')) {
      params.user = messagePerson.value.id
    }

    if (messagePerson.value?.mobileNumber != '' && messageType.value == "SMS") {
      params.to = messagePerson.value?.mobileNumber
    } else {
      params.to = messagePerson.value?.email
    }

    try {
      await pb.collection('userCommunications').create(params)
      successSent.value = true
      failSent.value = false
    } catch (e) {
      successSent.value = false
      failSent.value = true
    }
  }
}

const sendingLoading = ref(false)
const sendMessageToUsers = async() => {
  let userlist = bookingUsers.value
  if (checkMessageText() != '' && userlist.length > 0) {
    sendingLoading.value = true
    successSent.value = false
    failSent.value = false

    for(const user of userlist) {
      let params: any = {
        status: 'New',
        msgType: messageType.value,
        subject: messageSubject.value,
        message: messageText.value,
        booking: messageBookingDetails.value?.id || '',
        from: currentLocationDetails.value?.adminEmail || currentLocationDetails.value?.email || '',
      }

      if (user?.id && (user?.id != 'visitor')) {
        params.user = user.id
      }

      if (user?.mobileNumber != '' && messageType.value == "SMS") {
        params.to = user?.mobileNumber
      } else {
        params.to = user?.email
        params.msgType = "Email"

        if(messageType.value == "SMS"){
          params.message += "<br><p>SMS message sent to email due to no mobile number in profile</p>"
        }
      }

      try {
        await pb.collection('userCommunications').create(params)
        successSent.value = true
        failSent.value = false
      } catch (e) {
        successSent.value = false
        failSent.value = true
      }
    }
  }

  sendingLoading.value = false
}

const mobileFound = () => {
  let hasMobile = false
  if(messagePerson.value?.mobileNumber && messagePerson.value?.mobileNumber != ''){
    hasMobile = true
  }

  if(bookingUsers.value && bookingUsers.value.length > 0){
    bookingUsers.value.forEach((ul:any) => {
      if(ul?.mobileNumber && ul?.mobileNumber != ''){
        hasMobile = true
      }
    })
  }
  return hasMobile
}

onMounted(async ()=>{
  let tmpTemplates = await pb.collection('templates').getFullList()
  tmpTemplates.forEach( (tmpl:any) => {
    tmpl.selectname = tmpl.name + " - (" + tmpl.msgType + ")"
  })
  allTemplates.value  = tmpTemplates

  if(props?.bookingId && props?.bookingId != '') {
    await messageBooking(props?.bookingId)
  } else if(props?.userId && props?.userId != ''){
    await messageOwner(props?.userId)
  } else if(props?.users && props?.users?.length){
    await messageMembers(props.users)
  }
})

</script>

<template>
  <VDialog
    :max-width="1250"
    :model-value="messageDialog"
    persistent
    no-click-animation
  >
    <VCard>
      <div class="d-flex align-center justify-space-between bg-dialogHeaderColor">
        <VCardTitle>Message Member</VCardTitle>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="closeDialog()"
        />
      </div>
      <VCardText v-if="!isPreview">
        <div class="d-flex mb-5" v-if="!props?.users || props?.users?.length == 0">

          <VSelect label="Select User" class="me-2" :items="bookingUsers" v-model="personId"
                   @update:model-value="updateTemplateFields" item-title="name" item-value="id"></VSelect>
          <VTextField label="Email" class="ms-2 me-2" v-model="messagePerson.email" readonly disabled></VTextField>
          <VTextField label="Mobile" class="ms-2 me-2" v-model="messagePerson.mobileNumber" readonly disabled></VTextField>
          <VTextField label="First Name" class="ms-2" v-model="messagePerson.firstName" readonly disabled></VTextField>
          <VTextField label="Last Name" class="ms-2" v-model="messagePerson.lastName" readonly disabled></VTextField>
        </div>

        <div class="d-flex mb-5">
          <VSelect class="me-2" label="Message Type" :items="!mobileFound() ? ['Email'] : ['Email', 'SMS']"
                   @update:model-value="canEdit = false; nextTick(() => {canEdit = true})" v-model="messageType"></VSelect>
          <VSelect
            label="Select Message Template" class="me-2 ms-2" :items="allTemplates.filter(tmp => tmp.msgType == messageType)"
            @update:model-value="getTemplate" item-title="selectname" item-value="id"></VSelect>
          <VTextField label="Subject Line" class="ms-2" v-model="messageSubject"></VTextField>

        </div>

        <div class="mt-3" v-if="canEdit">
          <QuillEditor theme="snow" v-model:content="messageText" :content-type="messageType == 'Email' ? 'html' : 'text'" :toolbar="toolbarOptions()" />
        </div>

      </VCardText>
      <VCardText v-else>
        <h4 class="pb-3 mb-3" v-html="getPreviewText()[1]"></h4>
        <div class="pa-3 border rounded" v-html="getPreviewText()[0]">
        </div>
      </VCardText>
      <div id="messageCheck" v-html="messageText" class="d-none"></div>
      <VCardText v-if="successSent || failSent">
        <v-alert v-if="successSent" color="success">Message Has been Sent!</v-alert>
        <v-alert v-else color="error">Cannot Send Message</v-alert>
      </VCardText>

      <VCardActions class="mt-2">
        <VBtn
          :disabled="checkMessageText() == '' || messageTo == ''"
          color="success"
          variant="outlined"
          @click="isPreview = !isPreview">
          <span v-if="isPreview" class="me-1">End</span> Preview Message
        </VBtn>

        <VSpacer />
        <VBtn v-if="!props?.users || props?.users?.length == 0"
          :disabled="checkMessageText() == ''"
          color="primary"
          variant="outlined"
          :loading="sendingLoading"
          @click="sendMessage()"
        >
          Send Message <span v-if="bookingUsers.length > 1 && messagePerson?.firstName">to {{messagePerson?.name}}</span>
        </VBtn>

        <VBtn v-if="bookingUsers.length > 1"
          :disabled="checkMessageText() == '' || (messageTo == '' && props?.users?.length == 0)"
          color="primary"
          variant="outlined"
          @click="sendMessageToUsers()"
          :loading="sendingLoading"
        >
          Send Message to all users ({{bookingUsers.length}})
        </VBtn>

        <VBtn
          color="error"
          variant="outlined"
          @click="closeDialog()"
        >
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <component :is="'style'">
    {{getStyle()}}
  </component>
</template>


